import {
  STEP_IDS as AZURE_STEP_IDS,
  CONSENT_STATE_TTL_MS,
} from '../../components/AzureInstallationDialog/consts';
import { isAzureStateValid } from '../../components/AzureInstallationDialog/utils';
import { LOCAL_STORAGE_KEYS } from '../../consts/localStorage';
import { NAVIGATION_MODE } from './const';
import { NavigationMode, StepConfig } from './types';

export const canNavigateToStep = (
  targetIndex: number,
  steps: StepConfig[],
  navigationMode: NavigationMode,
  furthestStepReached: number,
  earliestStepAvailable: number,
) => {
  const step = steps[targetIndex];

  if (!step) return false;

  const azureConnectReposStepIndex = steps.findIndex(
    (s) => s.id === AZURE_STEP_IDS.CONNECT_REPOS,
  );

  if (isValidAzureRedirect(targetIndex, azureConnectReposStepIndex)) {
    return true;
  }

  // Linear mode: sidebar clicks are disabled, only Next/Back buttons work
  if (navigationMode === NAVIGATION_MODE.LINEAR) {
    return false;
  }

  // Non-linear mode: can click any step freely
  if (navigationMode === NAVIGATION_MODE.NON_LINEAR) {
    return true;
  }

  // Forward-only mode: can only navigate to steps already reached
  // can go back to previous steps, but can't skip ahead
  return (
    targetIndex >= earliestStepAvailable && targetIndex <= furthestStepReached
  );
};

/**
 * Validates if navigation to step 3 (Connect Repositories) is allowed via Azure redirect.
 * This special case allows skipping the normal progression validation when users
 * return from Azure's admin consent flow.
 *
 * Flow:
 * 1. User clicks "Grant Admin Consent" on step 2
 * 2. State is stored in localStorage with timestamp
 * 3. User is redirected to Azure AD for consent
 * 4. Azure redirects back to step 3 with ?success=true or ?error=...
 * 5. This function validates the stored state exists and hasn't expired
 *
 * @param targetIndex - The step index being navigated to
 * @param connectReposStepIndex - The index of the Connect Repositories step (2)
 * @returns true if this is a valid Azure redirect to step 3
 */
export const isValidAzureRedirect = (
  targetIndex: number,
  connectReposStepIndex: number,
): boolean => {
  // Only apply special logic for step 3 (Connect Repositories)
  if (targetIndex !== connectReposStepIndex) {
    return false;
  }

  const urlSearchParams = new URLSearchParams(window.location.search);

  // Check if URL has success or error parameter (indicating Azure redirect)
  const hasSuccessParam = urlSearchParams.has('success');
  const hasErrorParam =
    urlSearchParams.has('error') || urlSearchParams.has('error_description');

  if (!hasSuccessParam && !hasErrorParam) {
    return false;
  }

  // Check if state exists in localStorage
  const storedStateString = localStorage.getItem(
    LOCAL_STORAGE_KEYS.AZURE_INSTALLATION_STATE,
  );

  if (!storedStateString) {
    return false;
  }

  try {
    const storedState = JSON.parse(storedStateString);

    // Validate state has required fields and hasn't expired
    const hasRequiredFields =
      storedState.secret && storedState.workspace_id && storedState.timestamp;

    if (!hasRequiredFields) {
      return false;
    }

    // Check if state is still valid (less than 30 minutes old)
    const age = Date.now() - storedState.timestamp;
    const isNotExpired = age < CONSENT_STATE_TTL_MS;

    if (!isNotExpired) {
      return false;
    }

    // Validate URL state param matches the backend-generated state (CSRF check)
    return isAzureStateValid();
  } catch (error) {
    console.error('Failed to parse Azure consent state:', error);

    return false;
  }
};

export const getProgress = (currentStepIndex: number, steps: StepConfig[]) =>
  currentStepIndex >= 0 && steps.length > 0
    ? ((currentStepIndex + 1) * 100) / steps.length
    : 0;
